import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "heightField",
    "homologationFields",
    "heightWarning",
    "typeSelect",
    "heightInput",
    "mainPhotoInput",
    "mainPhotoPreview",
    "mainPhotoPreviewImg",
    "tabContent",
    "tabLink"
  ]

  connect() {
    this.toggleHeightFieldVisibility()
    this.initializeHomologationToggle()
    this.initializeReservationToggle()
    this.activateTabFromHash()
  }

  // Ouvre l'onglet demandé par l'ancre de l'URL (ex. /edit#sponsors),
  // pour que les liens profonds vers un onglet fonctionnent.
  activateTabFromHash() {
    const hash = window.location.hash.replace("#", "")
    if (!hash) return

    const tabLink = document.querySelector(`.tab-link[data-tab="${hash}"]`)
    if (!tabLink) return

    // Masquer tous les contenus, afficher celui de l'ancre
    document.querySelectorAll('.tab-content').forEach(content => {
      content.classList.add('hidden')
    })
    const selectedContent = document.querySelector(`[data-tab-content="${hash}"]`)
    if (selectedContent) {
      selectedContent.classList.remove('hidden')
    }

    this.activateTabLink(tabLink)
  }

  // P52 — l'onglet courant porte .is-active (composant .tab-nuit) : plus
  // de couleurs claires posées en JS.
  activateTabLink(tabLink) {
    document.querySelectorAll('.tab-link').forEach(link => {
      link.classList.remove('is-active')
      link.removeAttribute('aria-current')
    })
    tabLink.classList.add('is-active')
    tabLink.setAttribute('aria-current', 'page')
  }

  toggleHeightField() {
    this.toggleHeightFieldVisibility()
  }

  toggleHeightFieldVisibility() {
    if (this.hasTypeSelectTarget) {
      const isInterieur = this.typeSelectTarget.value === "interieur"
      if (this.hasHeightFieldTarget) {
        this.heightFieldTarget.classList.toggle("hidden", !isInterieur)
      }
    }
  }

  toggleHomologationFields(event) {
    const isHomologue = event.target.checked
    if (this.hasHomologationFieldsTarget) {
      this.homologationFieldsTarget.classList.toggle("hidden", !isHomologue)
    }
  }

  checkHeight() {
    if (!this.hasHeightInputTarget || !this.hasHeightWarningTarget) return

    const height = parseFloat(this.heightInputTarget.value)
    const warningDiv = this.heightWarningTarget
    // P52 — dans la nuit (espace gestionnaire), les encadrés nuit ; ailleurs
    // (ancien formulaire clair des terrains), les encadrés clairs.
    const nuit = !!this.element.closest("[data-theme=\"nuit\"]")
    const boite = (ton) => nuit
      ? (ton === "alerte" ? "flash-nuit-alert mb-0" : "flash-nuit-warning mb-0")
      : (ton === "alerte" ? "bg-red-50 border border-red-200 rounded-md p-3" : "bg-orange-50 border border-orange-200 rounded-md p-3")
    const encre = (ton) => nuit ? "" : (ton === "alerte" ? "text-red-800" : "text-orange-800")

    if (!height || height === 0) {
      warningDiv.classList.add("hidden")
      return
    }

    if (height < 6) {
      warningDiv.innerHTML = `
        <div class="${boite("alerte")}">
          <div class="flex">
            <svg class="h-5 w-5 flex-shrink-0 ${nuit ? "" : "text-red-400"}" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <p class="ml-3 text-sm ${encre("alerte")}">
              Hauteur inférieure au minimum FFT de 6m. Ce terrain ne pourra pas être homologué.
            </p>
          </div>
        </div>
      `
      warningDiv.classList.remove("hidden")
    } else if (height < 8) {
      warningDiv.innerHTML = `
        <div class="${boite("avertissement")}">
          <div class="flex">
            <svg class="h-5 w-5 flex-shrink-0 ${nuit ? "" : "text-orange-400"}" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <p class="ml-3 text-sm ${encre("avertissement")}">
              Hauteur inférieure à la hauteur recommandée de 8m. Homologation possible mais pas optimale.
            </p>
          </div>
        </div>
      `
      warningDiv.classList.remove("hidden")
    } else {
      warningDiv.classList.add("hidden")
    }
  }

  // === Gestion des onglets ===
  switchTab(event) {
    event.preventDefault()
    const tabName = event.currentTarget.dataset.tab

    // Masquer tous les contenus d'onglets
    document.querySelectorAll('.tab-content').forEach(content => {
      content.classList.add('hidden')
    })

    // Afficher le contenu de l'onglet sélectionné
    const selectedContent = document.querySelector(`[data-tab-content="${tabName}"]`)
    if (selectedContent) {
      selectedContent.classList.remove('hidden')
    }

    this.activateTabLink(event.currentTarget)
  }

  // === Gestion de la photo principale ===
  previewMainPhoto(event) {
    const file = event.target.files[0]

    if (!file) {
      return
    }

    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader()

      reader.onload = (e) => {
        if (this.hasMainPhotoPreviewTarget && this.hasMainPhotoPreviewImgTarget) {
          this.mainPhotoPreviewTarget.classList.remove('hidden')
          this.mainPhotoPreviewImgTarget.src = e.target.result
        }
      }

      reader.readAsDataURL(file)
    }
  }

  removeMainPhoto(event) {
    event.preventDefault()

    if (confirm('Êtes-vous sûr de vouloir supprimer la photo principale ?')) {
      // Marquer pour suppression
      const deleteInput = document.createElement('input')
      deleteInput.type = 'hidden'
      deleteInput.name = 'court[remove_photo_principale]'
      deleteInput.value = '1'
      this.element.appendChild(deleteInput)

      // Cacher la photo actuelle
      event.target.closest('div').style.display = 'none'

      // Réinitialiser l'input file
      if (this.hasMainPhotoInputTarget) {
        this.mainPhotoInputTarget.value = ''
      }
    }
  }

  // === Gestion des photos additionnelles ===
  async deletePhoto(event) {
    event.preventDefault()

    if (!confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
      return
    }

    const photoId = event.currentTarget.dataset.photoId
    const url = event.currentTarget.dataset.url

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        // Turbo Stream va automatiquement mettre à jour le DOM
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      } else {
        alert('Erreur lors de la suppression de la photo')
      }
    } catch (error) {
      console.error('Erreur:', error)
      alert('Erreur lors de la suppression de la photo')
    }
  }

  // === Toggles conditionnels ===
  toggleHauteurField(event) {
    const hauteurField = document.getElementById('hauteur-field')
    if (hauteurField) {
      if (event.target.value === 'interieur') {
        hauteurField.classList.remove('hidden')
      } else {
        hauteurField.classList.add('hidden')
      }
    }
  }

  toggleReservationLink(event) {
    const reservationField = document.getElementById('reservation-link-field')
    if (reservationField) {
      if (event.target.checked) {
        reservationField.classList.remove('hidden')
      } else {
        reservationField.classList.add('hidden')
      }
    }
  }

  initializeHomologationToggle() {
    const checkbox = document.getElementById('court_homologue')
    const fields = document.getElementById('homologation-fields')

    if (checkbox && fields) {
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
          fields.classList.remove('hidden')
        } else {
          fields.classList.add('hidden')
        }
      })
    }
  }

  initializeReservationToggle() {
    const checkbox = document.querySelector('input[name="court[reservation_en_ligne]"]')
    const field = document.getElementById('reservation-link-field')

    if (checkbox && field) {
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
          field.classList.remove('hidden')
        } else {
          field.classList.add('hidden')
        }
      })
    }
  }

  // === Utilitaires ===
  updatePhotoCount() {
    const currentCount = document.querySelectorAll('[data-photo-id]').length
    const maxPhotos = parseInt(document.querySelector('[data-max-photos]')?.dataset.maxPhotos || 5)

    // Mettre à jour le compteur dans l'onglet
    const tabLabel = document.querySelector('[data-tab="photos"]')
    if (tabLabel) {
      const match = tabLabel.textContent.match(/Photos \((\d+)\/(\d+)\)/)
      if (match) {
        tabLabel.textContent = `Photos (${currentCount}/${maxPhotos})`
      }
    }

    // Afficher/masquer la zone d'upload selon la limite
    const dropzone = document.querySelector('[data-controller="dropzone"]')
    const limitMessage = document.querySelector('[data-photos-limite]')

    if (currentCount >= maxPhotos) {
      if (dropzone) dropzone.style.display = 'none'
      if (limitMessage) limitMessage.style.display = 'block'
    } else {
      if (dropzone) dropzone.style.display = 'block'
      if (limitMessage) limitMessage.style.display = 'none'
    }
  }
}